/**
 * Picking the PIM product behind a personalised campaign's proofsheet button.
 *
 * Opens the platform's own PIM product picker and configurator in "configure
 * only" mode (no order type: OrderUtils resolves with the configuration instead
 * of creating an offer) and turns the result into what the backend stores.
 * Shared by the creation form and the campaign page.
 *
 * The picker and the configurator are Bootbox (Bootstrap) dialogs appended to
 * <body>, while the mailing modal lives inside the page — and z-index cannot
 * be trusted across that boundary (any positioned ancestor of the page is its
 * own stacking context). So when opened from inside the mailing modal, the
 * modal is simply hidden while one of those dialogs is open, and shown again
 * as soon as none is: after the configuration, or after a cancel. Between the
 * picker closing and the configurator opening (the product is fetched in
 * between) it may reappear for a moment; the next dialog hides it again.
 */
define('mailingProofsheetProduct',[
  'require',
  'module',
  'underscore',
  'jquery',
  'settings',
  'mailingShell'
], function (
  require,
  module,
  _,
  $,
  Settings,
  Shell
) {
  'use strict';

  var ProofsheetProduct = {

    /**
     * @param $modal the mailing modal to lower while the dialogs are open, or null
     * @return promise of { proofsheetProductRef, proofsheetProductName, proofsheetConfiguration, webshopUrls }
     *         — never settles when the user cancels the picker
     */
    choose: function ($modal) {
      var result = $.Deferred();
      var hide = function () {
        if ($modal) { $modal.css('visibility', 'hidden'); }
      };
      var show = function () {
        if ($modal) { $modal.css('visibility', ''); }
      };
      var anyDialogOpen = function () {
        return $('.modal.in, .modal:visible').length > 0;
      };
      var restore = function () {
        $(document).off('.mlgProofsheetProduct');
        show();
      };
      if ($modal) {
        $(document).off('.mlgProofsheetProduct')
          .on('shown.bs.modal.mlgProofsheetProduct', hide)
          .on('hidden.bs.modal.mlgProofsheetProduct', function () {
            // Bootstrap fires "hidden" before the next dialog is in: let it land first.
            window.setTimeout(function () {
              if (!anyDialogOpen()) { show(); }
            }, 250);
          });
        hide();
      }
      // Literal module name: the release build finds lazy requires by static analysis.
      require(['orderUtils'], function (OrderUtils) {
        var configured = $.Deferred();
        configured.done(function (configuration, productModel) {
          restore();
          if (!configuration || !productModel) { return; }
          result.resolve({
            // The reseller code, as the kanban's own PIM offer creation passes it.
            proofsheetProductRef: productModel.get('cCode') || productModel.get('code'),
            proofsheetProductName: productModel.get('name') || productModel.get('cCode') || productModel.get('code'),
            proofsheetConfiguration: JSON.stringify(ProofsheetProduct.toProductConfiguration(configuration)),
            // The backend has no locale → webshop map of its own; this one lets it send
            // a customer to the same webshop's proofsheet as the dashboard's link does.
            webshopUrls: JSON.stringify(Settings.get('webshopUrl') || {})
          });
        });
        // PIM products only: straight to the PIM picker, no product-type question.
        OrderUtils.openProductDialog(configured, null, true, null);
      });
      return result.promise();
    },

    /**
     * The configurator's output flattened to ProductConfigurationJson — the same
     * shape entities/orders.js createFromConfiguration posts. Files and comments
     * are left out on purpose: they belong to one order, not to every recipient's.
     */
    toProductConfiguration: function (configuration) {
      var currentUser = Settings.get('currentUser');
      var config = {
        quantity: Number(configuration.quantity) || 0,
        options: [],
        optionsMix: [],
        printZonesColors: {},
        // The salesperson who configured it gets the offers.
        userpidSecId: currentUser ? currentUser.secId : null
      };
      _.each(configuration.options || {}, function (value) {
        if (value !== null && value !== undefined) { config.options.push(value); }
      });
      _.each(configuration.optionsMix || {}, function (mix) { config.optionsMix.push(mix); });
      _.each(configuration.printZones || {}, function (zone) { config.printZonesColors[zone.pzId] = zone.nbrColor; });
      return config;
    },

    /** "Mug 330 ml · 100 pcs · 2 options · 1 print zone" */
    summary: function (name, configurationJson) {
      var configuration = {};
      try { configuration = JSON.parse(configurationJson || '{}'); } catch (error) { configuration = {}; }
      var options = (configuration.options || []).length + (configuration.optionsMix || []).length;
      var zones = _.size(configuration.printZonesColors || {});
      return _.compact([
        name,
        configuration.quantity ? Shell.formatNumber(configuration.quantity) + ' pcs' : null,
        options + ' option' + (options === 1 ? '' : 's'),
        zones + ' print zone' + (zones === 1 ? '' : 's')
      ]).join(' · ');
    }
  };

  module.exports = ProofsheetProduct;
});

